<template>
  <div class="config-manage-detail">
    <div class="detail-header">
      <a-button type="link" @click="handleBack" class="back-btn">
        <a-icon type="left" /> 返回
      </a-button>
      <h2>{{ configData.name || '配置详情' }}</h2>
    </div>

    <div class="detail-content" v-if="!loading">
      <!-- 基本信息 -->
      <a-card title="基本信息" :bordered="false" style="margin-bottom: 20px">
        <a-descriptions :column="2" bordered>
          <a-descriptions-item label="配置名称">
            {{ configData.name || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="配置备注">
            {{ configData.desc || '-' }}
          </a-descriptions-item>
        </a-descriptions>
      </a-card>

      <!-- 媒体资源 -->
      <a-card title="媒体资源" :bordered="false" style="margin-bottom: 20px">
        <a-descriptions :column="2" bordered>
          <a-descriptions-item label="开机图片">
            <div v-if="configData.bootImage" class="image-preview">
              <img :src="configData.bootImage.downloadUrl" alt="开机图片" />
            </div>
            <span v-else class="no-data">无</span>
          </a-descriptions-item>
          <a-descriptions-item label="4K开机图片">
            <div v-if="configData.bootImage" class="image-preview">
              <img :src="configData.bootImage4k.downloadUrl" alt="开机图片" />
            </div>
            <span v-else class="no-data">无</span>
          </a-descriptions-item>
          <a-descriptions-item label="开机视频">
            <div v-if="configData.bootAnimation" class="video-preview">
              <a-tag color="blue">{{ configData.bootAnimation.name }}</a-tag>
            </div>
            <span v-else class="no-data">无</span>
          </a-descriptions-item>
        </a-descriptions>
      </a-card>

      <!-- 主页应用 -->
      <a-card title="主页应用" :bordered="false" style="margin-bottom: 20px">
        <a-descriptions bordered>
          <a-descriptions-item label="应用列表">
            <div class="app-tags">
              <template v-if="configData.mainApp">
                <a-tag
                  color="blue"
                >
                  {{ configData.mainApp.name }}
                </a-tag>
              </template>
              <span v-else class="no-data">无</span>
            </div>
          </a-descriptions-item>
        </a-descriptions>
      </a-card>

      <!-- 预装应用 -->
      <a-card title="预装应用" :bordered="false" style="margin-bottom: 20px">
        <a-descriptions bordered>
          <a-descriptions-item label="应用列表">
            <div class="app-tags">
              <template v-if="configData.appList && configData.appList.length > 0">
                <a-tag
                  v-for="(app, index) in configData.appList"
                  :key="index"
                  color="blue"
                >
                  {{ index + 1 }}. {{ app.name }}
                </a-tag>
              </template>
              <span v-else class="no-data">无</span>
            </div>
          </a-descriptions-item>
        </a-descriptions>
      </a-card>

      <!-- 菜单配置 -->
      <a-card title="菜单配置" :bordered="false" style="margin-bottom: 20px">
        <a-descriptions :column="2" bordered>
          <a-descriptions-item label="配置类型">
            <a-tag :color="configData.menuConfigType === 0 ? 'green' : 'blue'">
              {{ configData.menuConfigType === 0 ? '本地上传' : '云端配置' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="配置内容">
            <template v-if="configData.menuConfigType === 0">
              {{ configData.compressedPackageName || '-' }}
            </template>
            <template v-else-if="configData.menuConfigType === 1">
              {{ configData.cloudConfig || '-' }}
            </template>
            <span v-else class="no-data">无</span>
          </a-descriptions-item>
        </a-descriptions>
      </a-card>

      <!-- 详细配置（云端配置时显示） -->
      <a-card
        title="详细配置"
        :bordered="false"
        v-if="configData.menuConfigType === 1"
        style="margin-bottom: 20px"
      >
        <a-descriptions :column="3" bordered>
          <a-descriptions-item label="定制">
            <a-tag :color="configData.customize === 'on' ? 'green' : 'red'">
              {{ configData.customize === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>

        <a-divider orientation="left">开机设置</a-divider>

        <a-descriptions :column="3" bordered>
          <a-descriptions-item label="系统屏保">
            {{ configData.screenSaverType === 'custom' ? '自定义' : '默认' }}
          </a-descriptions-item>
          <a-descriptions-item label="最大音量">
            {{ configData.maxVolume || 50 }}
          </a-descriptions-item>
          <a-descriptions-item label="调谐锁定">
            <a-tag :color="configData.tuningLock === 'on' ? 'green' : 'red'">
              {{ configData.tuningLock === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="TV返回主场景">
            <a-tag :color="configData.tvReturnMain === 'on' ? 'green' : 'red'">
              {{ configData.tvReturnMain === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="屏蔽WiFi">
            <a-tag :color="configData.blockWifi === 'on' ? 'green' : 'red'">
              {{ configData.blockWifi === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="屏蔽蓝牙">
            <a-tag :color="configData.blockBluetooth === 'on' ? 'green' : 'red'">
              {{ configData.blockBluetooth === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="待机灯状态">
            <a-tag :color="configData.standbyLight === 'on' ? 'green' : 'red'">
              {{ configData.standbyLight === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>

        <a-divider orientation="left">按键锁定</a-divider>

        <a-descriptions :column="4" bordered>
          <a-descriptions-item label="本机键锁">
            <a-tag :color="configData.localKeyLock === 'on' ? 'green' : 'red'">
              {{ configData.localKeyLock === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="主机键锁">
            <a-tag :color="configData.hostKeyLock === 'on' ? 'green' : 'red'">
              {{ configData.hostKeyLock === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="节目源键锁">
            <a-tag :color="configData.sourceKeyLock === 'on' ? 'green' : 'red'">
              {{ configData.sourceKeyLock === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="VIP键锁">
            <a-tag :color="configData.vipKeyLock === 'on' ? 'green' : 'red'">
              {{ configData.vipKeyLock === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="UMAX键锁">
            <a-tag :color="configData.umaxKeyLock === 'on' ? 'green' : 'red'">
              {{ configData.umaxKeyLock === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="设置键锁">
            <a-tag :color="configData.settingKeyLock === 'on' ? 'green' : 'red'">
              {{ configData.settingKeyLock === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>

        <a-divider orientation="left">功能限制</a-divider>

        <a-descriptions :column="4" bordered>
          <a-descriptions-item label="网络设置许可">
            <a-tag :color="configData.networkSetting === 'on' ? 'green' : 'red'">
              {{ configData.networkSetting === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="应用安装许可">
            <a-tag :color="configData.appInstall === 'on' ? 'green' : 'red'">
              {{ configData.appInstall === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="多屏互动许可">
            <a-tag :color="configData.multiScreen === 'on' ? 'green' : 'red'">
              {{ configData.multiScreen === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="屏保启动许可">
            <a-tag :color="configData.screenSaver === 'on' ? 'green' : 'red'">
              {{ configData.screenSaver === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="adb调试开关">
            <a-tag :color="configData.adbDebug === 'on' ? 'green' : 'red'">
              {{ configData.adbDebug === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="无信号关机">
            <a-tag :color="configData.noSignalShutdown === 'on' ? 'green' : 'red'">
              {{ configData.noSignalShutdown === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="USB禁用">
            <a-tag :color="configData.usbDisable === 'on' ? 'green' : 'red'">
              {{ configData.usbDisable === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>

        <a-divider orientation="left">开机模式</a-divider>

        <a-descriptions :column="3" bordered>
          <a-descriptions-item label="开机模式">
            <a-tag :color="configData.bootMode === 'on' ? 'green' : 'red'">
              {{ configData.bootMode === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="开机音量">
            {{ configData.bootVolume || 7 }}
          </a-descriptions-item>
          <a-descriptions-item label="开机源">
            {{ configData.bootSource || 'HDMI 2' }}
          </a-descriptions-item>
          <a-descriptions-item label="ATV开机频道">
            {{ configData.atvChannel || 1 }}
          </a-descriptions-item>
          <a-descriptions-item label="DTV开机频道">
            {{ configData.dtvChannel || 1 }}
          </a-descriptions-item>
          <a-descriptions-item label="开机全屏播放">
            <a-tag :color="configData.fullScreenPlay === 'on' ? 'green' : 'red'">
              {{ configData.fullScreenPlay === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="开机视频声音">
            <a-tag :color="configData.bootVideoSound === 'on' ? 'green' : 'red'">
              {{ configData.bootVideoSound === 'on' ? '开' : '关' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="开机直达应用" :span="2">
            {{ configData.bootApp || 'com.changhong.hotelv3' }}
          </a-descriptions-item>
        </a-descriptions>
      </a-card>
    </div>
    <div v-else class="loading-container">
      <a-spin size="large" />
    </div>
  </div>
</template>

<script>
import { getConfigManageDetail } from '@/api/configManage'

export default {
  name: 'ConfigManageDetail',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    configId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      configData: null,
      showEditModal: false,
      loading: false
    }
  },
  watch: {
    visible: {
      handler (newVal) {
        if (newVal && this.configId) {
          this.fetchConfigDetail()
        }
      },
      immediate: true
    },
    configId: {
      handler (newVal) {
        if (newVal && this.visible) {
          this.fetchConfigDetail()
        }
      }
    }
  },
  methods: {
    async fetchConfigDetail () {
      if (!this.configId) return
      this.loading = true
      try {
        // 模拟API调用，实际使用时取消注释
        const response = await getConfigManageDetail(this.configId)
        this.configData = response.data
      } catch (error) {
        console.error('获取配置详情失败:', error)
        this.$message.error('获取配置详情失败')
      } finally {
        this.loading = false
      }
    },
    handleBack () {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.config-manage-detail {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: 100vh;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 0 20px;
}

.detail-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

.detail-content {
  padding: 0 20px;
}

/* 图片和视频预览样式 */
.image-preview {
  width: 120px;
  height: 40px;
  overflow: hidden;
  border-radius: 4px;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-preview {
  width: 120px;
  height: 80px;
}

.video-preview video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

/* 应用标签样式 */
.app-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* 无数据样式 */
.no-data {
  color: #999;
  font-style: italic;
}

/* 加载容器 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}
</style>
